import { Document } from "models/schemas";
import React from "react";
import { PropertyFilterProps } from "@amzn/awsui-components-react/polaris";
import {
  FormField,
  DatePicker,
  DateInput,
  Calendar,
  Button,
} from "@amzn/awsui-components-react";
import { matchDateFilter } from "./utils";
import EmptyState from "components/empty-state";

export type AtlasDocumentFilteringProperty =
  PropertyFilterProps.FilteringProperty & {
    key:
      | keyof Document
      | "fileName"
      | "parsingStatus"
      | "updatedBy"
      | "updatedAt";
    getValueFormatter?: (valueOptions: any) => any;
    propertyValues?: string[];
  };

export const deleteInfo = (docs: Document[], suffix: string): string => {
  if (docs.length === 0) {
    return "";
  }
  const linkNum = docs.filter((d) => d.extentionType === "Link").length;
  const folderNum = docs.filter((d) => d.extentionType === "Folder").length;
  const fileNum = docs.filter(
    (d) => d.extentionType !== "Link" && d.extentionType !== "Folder"
  ).length;

  const parts: string[] = [];
  if (folderNum > 0) {
    parts.push(`${folderNum === 1 ? "folder" : "folders"}`);
  }
  if (linkNum > 0) {
    parts.push(`${linkNum === 1 ? "link" : "links"}`);
  }
  if (fileNum > 0) {
    parts.push(`${fileNum === 1 ? "file" : "files"}`);
  }

  let joinedParts: string;
  if (parts.length === 1) {
    joinedParts = parts[0];
  } else if (parts.length === 2) {
    joinedParts = parts.join(" and ");
  } else {
    joinedParts =
      parts.slice(0, -1).join(", ") + " and " + parts[parts.length - 1];
  }

  return `Delete ${joinedParts}${suffix}`;
};

export const modifiedComparator = (field: string) => {
  if (field === "team-files") {
    // For team files page we handle all sorting in useEffect to keep folders on top and not mix files and folders
    return (a: any, b: any) => {
      return 0;
    };
  }
  if (field === "fileName") {
    return (a: any, b: any) => {
      // For grouped
      if (Array.isArray(a.children) && Array.isArray(b.children)) {
        return 0;
      }
      // For flat and atlas kb
      else {
        return (a.fileName || "").localeCompare(b.fileName || "");
      }
    };
  }
  if (field === "updatedAt") {
    return (a: any, b: any) => {
      // For grouped
      if (Array.isArray(a.children) && Array.isArray(b.children)) {
        return 0;
      }
      // For flat and atlas kb
      else {
        const aTime = a.updatedAt
          ? new Date(Number(a.updatedAt)).getTime()
          : undefined;
        const bTime = b.updatedAt
          ? new Date(Number(b.updatedAt)).getTime()
          : undefined;
        if (aTime === undefined && bTime === undefined) return 0;
        if (aTime === undefined) return 1;
        if (bTime === undefined) return -1;
        return aTime - bTime;
      }
    };
  } else {
    throw new Error("document field name is unknown");
  }
};

// stable comparator instances to avoid AWS UI warnings
export const teamFilesFileNameComparator = modifiedComparator("team-files");
export const teamFilesUpdatedAtComparator = modifiedComparator("team-files");
export const fileNameComparator = modifiedComparator("fileName");
export const updatedAtComparator = modifiedComparator("updatedAt");

// Folders-first comparator that ensures folders always appear at the top
export const createFoldersFirstComparator = (
  sortField: string,
  isDescending: boolean
) => {
  return (a: any, b: any) => {
    const aIsFolder = a.extentionType === "Folder";
    const bIsFolder = b.extentionType === "Folder";

    if (aIsFolder && !bIsFolder) return -1;
    if (!aIsFolder && bIsFolder) return 1;

    // Apply actual sort for docs of same type (file/folder)
    let result = 0;

    if (sortField === "fileName") {
      result = (a.fileName || "").localeCompare(b.fileName || "");
    } else if (sortField === "updatedAt") {
      const aTime = a.updatedAt ? new Date(Number(a.updatedAt)).getTime() : 0;
      const bTime = b.updatedAt ? new Date(Number(b.updatedAt)).getTime() : 0;
      result = aTime - bTime;
    } else if (sortField === "updatedBy") {
      result = (a.updatedBy || "").localeCompare(b.updatedBy || "");
    }

    return isDescending ? -result : result;
  };
};

export const pageSizePreferenceGaiaObject = {
  title: "Page size",
  options: [
    { value: 10, label: "10 items" },
    { value: 25, label: "25 items" },
    { value: 50, label: "50 items" },
  ],
};

export const pageSizePreferenceAtlasKB = {
  title: "Page size",
  options: [
    { value: 50, label: "50 items" },
    { value: 100, label: "100 items" },
    { value: 200, label: "200 items" },
  ],
};

export const createAtlasFilterProperties = (
  flatDocs: Document[]
): AtlasDocumentFilteringProperty[] => {
  const ATLAS_FILTER_PROPERTIES: AtlasDocumentFilteringProperty[] = [
    {
      propertyLabel: "Name",
      key: "fileName",
      groupValuesLabel: "Name",
      operators: [":", "=", "^", "!:", "!=", "!^"],
    },
    {
      propertyLabel: "Size",
      key: "size",
      groupValuesLabel: "Size values",
      operators: [":", "=", "^", "!:", "!=", "!^"],
    },
    {
      propertyLabel: "Uploaded On",
      key: "updatedAt",
      groupValuesLabel: "Uploaded on values",
      operators: ["=", "!=", "<", "<=", ">", ">="].map((operator) => ({
        operator,
        form: ({ value, onChange, filter }) => {
          const handleDateChange = (dateString: string) => {
            if (dateString) {
              const date = new Date(dateString);
              onChange(date.toISOString().split("T")[0]);
            } else {
              onChange("");
            }
          };
          if (typeof filter === "undefined") {
            return (
              <FormField>
                <DatePicker
                  value={value ?? ""}
                  onChange={(event) => handleDateChange(event.detail.value)}
                  placeholder="YYYY/MM/DD"
                  locale="en-GB"
                />
              </FormField>
            );
          }
          return (
            <div className="date-form">
              <FormField>
                <DateInput
                  value={value ?? ""}
                  onChange={(event) => handleDateChange(event.detail.value)}
                  placeholder="YYYY/MM/DD"
                />
              </FormField>
              <Calendar
                value={value ?? ""}
                onChange={(event) => handleDateChange(event.detail.value)}
                locale="en-GB"
              />
            </div>
          );
        },
        match: (itemValue, filterValue) =>
          matchDateFilter(itemValue, filterValue, operator),
      })),
    },
    {
      propertyLabel: "Uploaded By",
      key: "updatedBy",
      groupValuesLabel: "Uploaded by values",
      operators: [":", "=", "^", "!:", "!=", "!^"],
      propertyValues: Array.from(
        new Set(flatDocs.map((doc) => (doc as any).updatedBy).filter(Boolean))
      ).sort(),
    },
    {
      propertyLabel: "Parsing Status",
      key: "parsingStatus",
      groupValuesLabel: "Parsing status values",
      operators: [
        { operator: "=", tokenType: "enum" },
        { operator: "!=", tokenType: "enum" },
      ],
      propertyValues: Array.from(
        new Set(
          flatDocs.map((doc) => (doc as any).parsingStatus).filter(Boolean)
        )
      ).sort(),
    },
  ];

  return ATLAS_FILTER_PROPERTIES;
};

export const createCollectionConfig = (options: {
  useAtlasEndpoints: boolean;
  enablePropertyFilter: boolean;
  filteringProperties: AtlasDocumentFilteringProperty[];
  filteringState: any;
  preferences: { pageSize: number };
  rawColumnDefinitions: any[];
  displayMode: string;
  actions: any;
}) => {
  const {
    useAtlasEndpoints,
    enablePropertyFilter,
    filteringProperties,
    filteringState,
    preferences,
    rawColumnDefinitions,
    displayMode,
    actions,
  } = options;

  if (useAtlasEndpoints && enablePropertyFilter) {
    return {
      propertyFiltering: {
        filteringProperties: filteringProperties,
        empty: (
          <EmptyState
            title="No files"
            subtitle="No files to display."
            action={<Button onClick={() => {}}>Button</Button>}
          />
        ),
        noMatch: (
          <EmptyState
            title="No matches"
            subtitle="We can't find a match."
            action={
              <Button
                onClick={() =>
                  actions.setPropertyFiltering({
                    tokens: [],
                    operation: "and",
                  })
                }
              >
                Clear filter
              </Button>
            }
          />
        ),
        defaultQuery: filteringState,
      },
      pagination: { pageSize: preferences.pageSize },
      sorting: {
        defaultState: {
          sortingColumn: rawColumnDefinitions.find(
            (col) => col.sortingField === "updatedAt"
          )!,
          isDescending: true,
        },
      },
    };
  } else {
    return {
      filtering: {
        filteringFunction(item: any, filteringText: string) {
          if (filteringText === undefined || filteringText === "") return true;

          const lowerCaseFilter = filteringText.toLowerCase();

          if (displayMode === "flat") {
            const checkField = (field: string | undefined): boolean => {
              return field
                ? field.toLowerCase().includes(lowerCaseFilter)
                : false;
            };

            return checkField(item.fileName) || checkField(item.extentionType);
          } else {
            // For grouped mode, search in category name and children
            const fileNameMatches = item.fileName
              .toLowerCase()
              .includes(lowerCaseFilter);

            const childrenMatch =
              Array.isArray(item.children) &&
              item.children.some((child: any) =>
                child.fileName.toLowerCase().includes(lowerCaseFilter)
              );
            return fileNameMatches || childrenMatch;
          }
        },
        empty: (
          <EmptyState
            title="No files"
            subtitle="No files to display."
            action={<Button onClick={() => {}}>Button</Button>}
          />
        ),
        noMatch: (
          <EmptyState
            title="No matches"
            subtitle="We cant find a match."
            action={
              <Button onClick={() => actions.setFiltering("")}>
                Clear filter
              </Button>
            }
          />
        ),
      },
      pagination: { pageSize: preferences.pageSize },
      sorting: useAtlasEndpoints
        ? {
            defaultState: {
              sortingColumn: rawColumnDefinitions.find(
                (col) => col.sortingField === "updatedAt"
              )!,
              isDescending: true,
            },
          }
        : {},
    };
  }
};
